import { Store } from "@reduxjs/toolkit";
import { RootState } from "src/store";
import { message } from "src/assets/imports/antdComponents";
import { getMetaDetails } from "src/pages/Studies/serviceHelper";
import { environment } from "src/config/environment";
import { getWebSocketAuthProtocols } from "src/services/wsAuth";
import { AutosaveStatusType, IDataSources, GlobalInstruction as IGlobalInstruction, IReportSection } from "../models/interface";
import { sanitizeForLog } from "src/utils/security";
import {
	setAllSectionsData,
	setIsLockedModalOpen,
	setLockedModalContent,
	setReportsReferences,
	setReportsReferencesStudyType,
	updateGlobalInstructions,
	updateSelectedReport,
	setReportsReferenceActiveTab,
	setIsLockedByAnotherUser,
} from "../states/reportsSlice";
import { userAccessType } from "src/pages/Studies/models/interfaces";
import { StudyType } from "src/pages/NewAskKural/models/interfaces";

export interface WebSocketMessage {
	type?: string;
	event?: string;
	[key: string]: any;
}

export interface WebSocketEvent {
	event?: string;
	type?: string;
	payload?: any;
	data?: any;
	message?: string;
	timestamp?: string;
	code?: number;
	reason?: string;
}

export interface EventCallback {
	onSuccess?: (data: any) => void;
	onError?: (data: any) => void;
}

interface OperationCallbacks {
	onSuccess?: (data: WebSocketEvent) => void;
	onError?: (data: WebSocketEvent) => void;
}

export type WebSocketMessageHandler = (data: any) => void;

class WebSocketReportService {
	private socket: WebSocket | null = null;
	private store: Store | null = null;
	private messageHandlers: Map<string, WebSocketMessageHandler[]> = new Map();
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 120; // 30 minutes at 15 seconds per attempt
	private reconnectTimeout: NodeJS.Timeout | null = null;
	private lastActivityTime: Date = new Date();
	private heartbeatInterval: NodeJS.Timeout | null = null;
	public currentReportIterationId: string | null = null;
	private locationChangeListener: (() => void) | null = null;
	private isReconnecting: boolean = false;
	private reconnectionLock: boolean = false;
	private connectionLostNotificationKey: string | number | null = null;
	private isReconnectedSocket: boolean = false;
	private pendingUpdates: WebSocketMessage[] = [];
	private errorHandled: boolean = false;
	private preserveConnectionOnNextNavigation: boolean = false;
	private originalPushState: typeof history.pushState | null = null;
	private originalReplaceState: typeof history.replaceState | null = null;
	private operationCallbacks: Map<string, OperationCallbacks> = new Map();

	private isConnecting: boolean = false; // NEW: Track connection state
	private initializationPromise: Promise<any> | null = null; // NEW: Track initialization promise

	private boundNetworkStatusChange: () => void;
	private networkListenersAttached: boolean = false;

	constructor() {
		this.boundNetworkStatusChange = this.handleNetworkStatusChange.bind(this);
		this.attachNetworkListeners();
	}

	private attachNetworkListeners() {
		if (this.networkListenersAttached) return;
		window.addEventListener("online", this.boundNetworkStatusChange);
		window.addEventListener("offline", this.boundNetworkStatusChange);
		this.networkListenersAttached = true;
	}

	private detachNetworkListeners() {
		if (!this.networkListenersAttached) return;
		window.removeEventListener("online", this.boundNetworkStatusChange);
		window.removeEventListener("offline", this.boundNetworkStatusChange);
		this.networkListenersAttached = false;
	}

	public isReconnectingNow(): boolean {
		return this.isReconnecting;
	}

	public isCurrentlyConnecting(): boolean {
		return this.isConnecting;
	}

	public getCurrentReportId(): string | null {
		return this.currentReportIterationId;
	}

	private handleNetworkStatusChange() {
		if (navigator.onLine) {
			// We're back online, attempt to reconnect if we have an active session
			if (
				this.currentReportIterationId &&
				!this.isReconnecting &&
				!this.isConnecting &&
				!this.reconnectionLock &&
				!this.initializationPromise &&
				(!this.socket || this.socket.readyState !== WebSocket.OPEN)
			) {
				this.isReconnecting = true;
				this.immediateReconnect(this.currentReportIterationId);
			}
		} else {
			// We're offline, show a message only when this service owns an active session
			if (this.currentReportIterationId && this.socket) {
				message.error({
					content: "Network connection lost. Will reconnect when available.",
					key: "network-connection-lost",
				});
			}
		}
	}

	public injectStore(store: Store) {
		this.store = store;
	}

	private getState(): RootState {
		if (!this.store) {
			throw new Error("Store not injected. Call injectStore first.");
		}
		return this.store.getState() as RootState;
	}

	private dispatch(action: any) {
		if (!this.store) {
			throw new Error("Store not injected. Call injectStore first.");
		}
		this.store.dispatch(action);
	}

	public preserveConnectionForNextNavigation() {
		this.preserveConnectionOnNextNavigation = true;
	}

	public handlePageLoadReconnection() {
		const state = this.getState();
		const reportIterationId = state.reports?.selectedReport?.report_iteration_id || this.currentReportIterationId;
		if (reportIterationId) {
			this.immediateReconnect(reportIterationId);
		}
	}

	private buildWebSocketUrl(reportIterationId: string): string {
		const state = this.getState();
		const profileId = state.studiesSlice?.userProfileId;
		const roleId = state.studiesSlice?.role;
		const params = new URLSearchParams({
			report_iteration_id: reportIterationId,
			...(profileId && { profile_id: profileId }),
			...(roleId && { role_id: roleId }),
		});

		return `${environment.REACT_APP_API_URL.replace(/^http/, "ws")}sb-reports/ws/reports?${params.toString()}`;
	}

	// Initialize and connect to the WebSocket
	public connect(reportIterationId: string, isReconnect: boolean = false): Promise<boolean> {
		// Only connect if reportIterationId is valid (not null, undefined, or empty)
		if (!reportIterationId || reportIterationId.trim() === "") {
			const error = new Error(`Invalid reportIterationId provided: ${reportIterationId}. WebSocket connection aborted.`);
			console.error("[WebSocketReportService] Connection failed:", error.message);
			return Promise.reject(error);
		}

		// Prevent multiple simultaneous connections
		if (this.isConnecting) {
			return Promise.resolve(true);
		}

		// Re-attach network listeners if a prior disconnect() removed them
		this.attachNetworkListeners();

		// Setup navigation change listener
		this.setupLocationChangeListener();

		return new Promise(async (resolve, reject) => {
			if (this.socket && this.socket.readyState === WebSocket.OPEN) {
				resolve(true);
				return;
			}

			this.isConnecting = true; // Set connecting flag
			this.currentReportIterationId = reportIterationId;
			this.isReconnectedSocket = isReconnect;
			this.errorHandled = false;

			try {
				const url = this.buildWebSocketUrl(reportIterationId);

				// For Cognito builds, pass the bearer token via `Sec-WebSocket-Protocol`
				// (the browser WebSocket API can't set a real Authorization header).
				// IDM builds rely on cookies, which the browser auto-attaches.
				const protocols = await getWebSocketAuthProtocols();
				this.socket = protocols ? new WebSocket(url, protocols) : new WebSocket(url);

				this.socket.onopen = () => {
					this.reconnectAttempts = 0;
					this.isReconnecting = false;
					this.isConnecting = false; // Clear connecting flag
					this.setupHeartbeat();

					// Show success message if this was a reconnection
					if (isReconnect) {
						message.success("Connection re-established successfully");
						// Resend any pending updates
						this.resendPendingUpdates();
					}
					resolve(true);
				};

				this.socket.onmessage = (event) => {
					this.lastActivityTime = new Date();
					let data;
					try {
						data = JSON.parse(event.data);
					} catch (err) {
						console.error("[WebSocketReportService] Failed to parse message data", event.data, err);
						return;
					}

					// Check for error about another user editing
					if ((data.type === "error" || data.event === "error") && data.message?.includes("Another user is editing")) {
						this.errorHandled = true;
						this.isConnecting = false; // Clear connecting flag
						const editConflictError = new Error(data.message);
						editConflictError.name = "EditConflictError";
						console.error("[WebSocketReportService] Edit conflict error", editConflictError);
						reject(editConflictError);
						return;
					}

					this.handleMessage(data);
				};

				this.socket.onerror = (error) => {
					console.error("[WebSocketReportService] WebSocket error:", error);
					this.isConnecting = false; // Clear connecting flag
					if (!this.errorHandled) {
						this.isReconnecting = true;
						if (navigator.onLine) {
							message.error("Connection lost. Attempting to reconnect...");
						}
						reject(error);
					}
				};

				this.socket.onclose = (event) => {
					this.isConnecting = false; // Clear connecting flag

					const closeData = {
						type: "socket_close",
						code: event.code,
						reason: event.reason,
					};
					this.handleMessage(closeData);

					if (event.code === 1003) {
						console.error("[WebSocketReportService] Socket closed with code 1003", event.reason);
						// Handle edit lock if needed
						// this.dispatch(setIsEditable({ isEditable: false }));

						const socketCloseError = new Error(event.reason || "Socket closed with code 1003");
						socketCloseError.name = "SocketCloseError";
						// @ts-ignore
						socketCloseError.code = 1003;

						// Set errorHandled flag
						this.errorHandled = true;

						// If the promise is still pending, reject it with this error
						reject(socketCloseError);
					}

					if (event.code === 1003 && event.reason?.includes("Another user is editing")) {
						message.error(event.reason);
						this.errorHandled = true;
					}

					if (!this.errorHandled && event.code !== 1000 && event.code !== 1001 && !(event.code === 1003 && event.reason?.includes("Another user is editing"))) {
						// If we're online, attempt to reconnect
						if (navigator.onLine && this.currentReportIterationId && !this.isReconnecting) {
							this.isReconnecting = true;
							this.attemptReconnect(this.currentReportIterationId);
						}
					} else {
						// Clean up connection resources but don't reset reportIterationId
						this.cleanupConnection(false);
					}
				};
			} catch (error) {
				console.error("[WebSocketReportService] Failed to connect to WebSocket:", error);
				this.isConnecting = false; // Clear connecting flag
				reject(error);
			}
		});
	}

	// Setup listener for location/navigation changes
	private setupLocationChangeListener() {
		// Remove any existing listener first
		this.removeLocationChangeListener();

		// Create a new history listener
		this.locationChangeListener = () => {
			// Check if we should preserve the connection
			if (this.preserveConnectionOnNextNavigation) {
				this.preserveConnectionOnNextNavigation = false;
				return; // Don't disconnect
			}

			this.disconnect();
		};

		// Add listener for popstate event (browser back/forward)
		window.addEventListener("popstate", this.locationChangeListener);

		// Store original methods if not already stored
		if (!this.originalPushState) {
			this.originalPushState = history.pushState;
		}
		if (!this.originalReplaceState) {
			this.originalReplaceState = history.replaceState;
		}

		// Monkey patch history methods to catch programmatic navigation
		const self = this;
		history.pushState = function (...args) {
			self.originalPushState!.apply(history, args);
			if (self.locationChangeListener) {
				self.locationChangeListener();
			}
		};

		history.replaceState = function (...args) {
			self.originalReplaceState!.apply(history, args);
			if (self.locationChangeListener) {
				self.locationChangeListener();
			}
		};
	}

	// Remove location change listener
	private removeLocationChangeListener() {
		if (this.locationChangeListener) {
			window.removeEventListener("popstate", this.locationChangeListener);
			this.locationChangeListener = null;

			// Restore original history methods
			if (this.originalPushState) {
				history.pushState = this.originalPushState;
			}
			if (this.originalReplaceState) {
				history.replaceState = this.originalReplaceState;
			}
		}
	}

	// Disconnect from the WebSocket
	public disconnect() {
		// Clear initialization promise
		this.initializationPromise = null;
		this.preserveConnectionOnNextNavigation = false;

		if (this.socket) {
			// Send close_connection message before closing the socket
			if (this.socket.readyState === WebSocket.OPEN) {
				this.send({
					type: "close_connection",
				});

				// Small delay to allow the message to be sent before closing
				setTimeout(() => {
					if (this.socket) {
						this.socket.close(1000, "Client initiated disconnect");
					}
				}, 100);
			} else {
				this.socket.close(1000, "Client initiated disconnect");
			}

			this.cleanupConnection(true);
		}

		// Close any existing connection notification
		if (this.connectionLostNotificationKey) {
			message.destroy(this.connectionLostNotificationKey);
			this.connectionLostNotificationKey = null;
		}

		// Remove navigation listener
		this.removeLocationChangeListener();

		// Remove window network listeners — re-attached on next connect()
		this.detachNetworkListeners();
	}

	// Register a message handler
	public on(messageType: string, handler: WebSocketMessageHandler) {
		if (!this.messageHandlers.has(messageType)) {
			this.messageHandlers.set(messageType, []);
		}
		this.messageHandlers.get(messageType)?.push(handler);
	}

	// Remove a message handler
	public off(messageType: string, handler: WebSocketMessageHandler) {
		if (this.messageHandlers.has(messageType)) {
			const handlers = this.messageHandlers.get(messageType) || [];
			const index = handlers.indexOf(handler);
			if (index !== -1) {
				handlers.splice(index, 1);
			}
		}
	}

	// Send a message to the WebSocket server
	public send(msg: WebSocketMessage): boolean {
		if (this.socket && this.socket.readyState === WebSocket.OPEN) {
			this.lastActivityTime = new Date();
			this.socket.send(JSON.stringify(msg));

			// Store update messages in case we need to resend after reconnection
			if (this.isUpdateMessage(msg.type)) {
				this.pendingUpdates.push(msg);
			}

			return true;
		} else if (this.isUpdateMessage(msg.type)) {
			// If the socket is not open but this is an update message, store it to send later
			this.pendingUpdates.push(msg);
			// If we're not already reconnecting, try to reconnect
			if (!this.isReconnecting && !this.isConnecting && this.currentReportIterationId) {
				message.error("Connection lost while updating. Reconnecting...");
				this.immediateReconnect(this.currentReportIterationId);
			}
		}
		return false;
	}

	// Helper to check if message type is an update operation
	private isUpdateMessage(type?: string): boolean {
		const updateTypes = [
			"section_update",
			"section_create",
			"section_delete",
			"sections_reorder",
			"global_instructions_update",
			"global_data_sources_update",
			"section_data_sources_update",
		];
		return !!type && updateTypes.includes(type);
	}

	// Resend pending updates after reconnection
	private resendPendingUpdates() {
		if (this.pendingUpdates.length > 0 && this.socket && this.socket.readyState === WebSocket.OPEN) {
			// Only send the most recent update to avoid conflicts
			const latestUpdate = this.pendingUpdates[this.pendingUpdates.length - 1];
			this.socket.send(JSON.stringify(latestUpdate));
		}
	}

	private sendWithCallbacks(
		operationType: string,
		message: WebSocketMessage,
		callbacks?: {
			onSuccess?: (data: any) => void;
			onError?: (data: any) => void;
		},
	): boolean {
		callbacks && this.operationCallbacks.set(operationType, callbacks);
		return this.send(message);
	}

	// Helper function to handle callback execution and cleanup
	private handleEventCallback(operationKey: string, isError: boolean, data: WebSocketEvent, defaultErrorMessage?: string): void {
		const callbacks = this.operationCallbacks.get(operationKey);

		if (isError) {
			if (callbacks?.onError) {
				callbacks.onError(data);
			} else if (defaultErrorMessage) {
				const errorMessage = data?.message || defaultErrorMessage;
				message.error(errorMessage);
			}
		} else {
			callbacks?.onSuccess?.(data);
		}

		this.operationCallbacks.delete(operationKey);
	}

	// In handleMessage method, replace lines 455-629 with:
	private async handleMessage(data: WebSocketEvent) {
		const eventType = data.event || data.type;

		// Handle standard message types
		if (eventType === "ping") {
			this.send({ type: "pong" });
			return;
		}

		if (eventType === "auth_refresh") {
			try {
				await getMetaDetails();
			} catch (error) {
				console.error("Error during auth refresh:", error);
			}
			return;
		}

		// Handle initial report data
		if (eventType === "initial_report_data") {
			if (!this.isReconnectedSocket && data.data) {
				const { structure, global_instruction, global_data_sources, global_data_access_mismatch, references } = data?.data || {};
				const transformedSections =
					structure?.sections?.map((section: any) => {
						const transformedSection = {
							...section,
							data: {
								key_question: section?.key_question ?? section?.data?.key_question ?? "",
								instructions: section?.instructions ?? section?.data?.instructions ?? "",
							},
							data_sources: section?.data_sources,
						};
						return transformedSection;
					}) ?? [];

				const transformedGlobalInstructions = {
					instructions:
						typeof global_instruction === "string" ? global_instruction : (global_instruction?.additional_instructions ?? global_instruction?.instructions ?? ""),
					data_sources: global_data_sources,
					data_access_mismatch: global_data_access_mismatch || false,
				};
				if (data.data.is_locked) {
					this.dispatch(updateSelectedReport({ user_access_type: userAccessType.view }));
					this.dispatch(setIsLockedModalOpen(true));
					this.dispatch(setIsLockedByAnotherUser({ status: data.data.is_locked, user: data.data?.locked_by }));
					this.dispatch(setLockedModalContent(`Locked by ${data.data?.locked_by?.user_name}. Opening in View mode`));
				}
				this.dispatch(setAllSectionsData(transformedSections));
				this.dispatch(updateGlobalInstructions(transformedGlobalInstructions));
				if (references && Object.keys(references)?.length > 0) {
					this.dispatch(setReportsReferences(references));
					this.dispatch(setReportsReferencesStudyType(Object.keys(references) as StudyType[]));
					this.dispatch(setReportsReferenceActiveTab(Object.keys(references)?.[0] as StudyType));
				}
			}
			this.isReconnectedSocket = false;
		}

		// Handle callback-based events with switch
		switch (eventType) {
			// Section create
			case "section_create_success":
				this.handleEventCallback("section_create", false, data);
				break;

			case "section_create_error":
				this.handleEventCallback("section_create", true, data, "Failed to create section");
				break;

			// Section delete
			case "section_delete_success":
				this.handleEventCallback("section_delete", false, data);
				break;
			case "section_delete_error":
				this.handleEventCallback("section_delete", true, data, "Failed to delete section");
				break;

			// Section update
			case "section_update_success":
				this.handleEventCallback("section_update", false, data);
				break;
			case "section_update_error":
				this.handleEventCallback("section_update", true, data, "Failed to update section");
				break;

			// Sections reorder
			case "sections_reorder_success":
				this.handleEventCallback("sections_reorder", false, data);
				break;
			case "sections_reorder_error":
				this.handleEventCallback("sections_reorder", true, data, "Failed to reorder sections");
				break;

			// Global data sources
			case "global_data_sources_update_success":
				this.handleEventCallback("global_data_sources_update", false, data);
				break;
			case "global_data_sources_update_error":
				this.handleEventCallback("global_data_sources_update", true, data, "Failed to update global data sources");
				break;

			// Global instructions
			case "global_instructions_update_success":
				this.handleEventCallback("global_instructions_update", false, data);
				break;
			case "global_instructions_update_error":
				this.handleEventCallback("global_instructions_update", true, data, "Failed to update global instructions");
				break;

			// Sectional data sources
			case "sectional_data_sources_update_success":
				this.handleEventCallback("section_data_sources_update", false, data);
				break;
			case "sectional_data_sources_update_error":
				this.handleEventCallback("section_data_sources_update", true, data, "Failed to update section data sources");
				break;
			default:
				break;
		}

		// Handle success confirmations
		// if (eventType?.endsWith("_success") && this.isUpdateMessage(eventType.replace("_success", ""))) {
		// 	this.pendingUpdates = [];
		// 	this.dispatch(markAsSaved());
		// 	this.dispatch(setUnsavedChanges(false));
		// }

		// Handle error messages
		if (eventType === "error") {
			console.error("WebSocket error from server:", data.message || data.payload);
			message.error(data.message || "An error occurred");
			return;
		}

		// Dispatch to registered handlers
		const handlers = this.messageHandlers.get(eventType) || [];
		handlers.forEach((handler) => {
			try {
				handler(data);
			} catch (error) {
				console.error(`Error in handler for message type "${sanitizeForLog(eventType)}":`, error);
			}
		});
	}

	// Setup heartbeat mechanism - FIXED VERSION
	// Setup heartbeat mechanism - FIXED VERSION
	private setupHeartbeat() {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
		}

		// Check connection every 15 seconds
		this.heartbeatInterval = setInterval(() => {
			if (this.socket && this.socket.readyState === WebSocket.OPEN) {
				const idleTime = (new Date().getTime() - this.lastActivityTime.getTime()) / 1000;
				if (idleTime > 1680) {
					// 28 minutes
					this.send({ type: "ping", timestamp: new Date().toISOString() });
				}
			} else if (
				this.socket &&
				this.socket.readyState === WebSocket.CLOSED &&
				!this.isReconnecting &&
				!this.isConnecting &&
				!this.reconnectionLock &&
				!this.initializationPromise
			) {
				if (this.currentReportIterationId && navigator.onLine) {
					this.isReconnecting = true;
					this.attemptReconnect(this.currentReportIterationId);
				}
			}
		}, 15000); // 15 seconds
	}

	// Cleanup connection resources
	private cleanupConnection(resetIds: boolean = true) {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}

		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		// Reset flags
		this.isConnecting = false;
		this.reconnectionLock = false;

		// If resetIds is true, clear the current session identifiers and pending updates
		if (resetIds) {
			this.currentReportIterationId = null;
			this.isReconnecting = false;
			this.pendingUpdates = [];
		}
	}

	// Immediate reconnect attempt (for when network comes back online)
	public immediateReconnect(reportIterationId: string) {
		// Don't reconnect if we're already connecting or connected or locked
		if (
			this.reconnectionLock ||
			this.isConnecting ||
			this.initializationPromise !== null ||
			(this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN))
		) {
			this.isReconnecting = false;
			return;
		}

		this.reconnectionLock = true;
		this.isReconnecting = true;

		// If we have a socket, but it's closed, create a new one
		if (this.socket) {
			if (this.socket.readyState !== WebSocket.CLOSED) {
				this.socket.close();
			}
			this.socket = null;
		}

		// Try to establish a new connection immediately with the reconnect flag
		this.connect(reportIterationId, true)
			.then(() => {
				this.isReconnecting = false;
				this.reconnectionLock = false;
			})
			.catch((error) => {
				console.error("Immediate reconnection failed:", error);
				this.reconnectionLock = false;
				// If immediate reconnection fails, fall back to the regular retry schedule
				if (navigator.onLine) {
					this.attemptReconnect(reportIterationId);
				} else {
					this.isReconnecting = false;
				}
			});
	}

	// Attempt to reconnect with scheduled retries
	private attemptReconnect(reportIterationId: string) {
		// Don't attempt to reconnect if we're offline or already connecting
		if (!navigator.onLine || this.isConnecting) {
			this.isReconnecting = false;
			return;
		}

		// If max attempts not reached yet
		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			// Clear any existing timeout
			if (this.reconnectTimeout) {
				clearTimeout(this.reconnectTimeout);
			}

			const delay = 15000; // 15 seconds

			this.reconnectTimeout = setTimeout(() => {
				this.reconnectAttempts++;

				// If we have a socket, but it's closed, create a new one
				if (this.socket) {
					if (this.socket.readyState !== WebSocket.CLOSED) {
						this.socket.close();
					}
					this.socket = null;
				}

				// Try to establish a new connection with the reconnect flag
				this.connect(reportIterationId, true)
					.then(() => {
						this.isReconnecting = false;
					})
					.catch((error) => {
						console.error("Reconnection attempt failed:", error);
						// If still online, schedule another attempt
						if (navigator.onLine) {
							this.attemptReconnect(reportIterationId);
						} else {
							this.isReconnecting = false;
						}
					});
			}, delay);
		} else {
			console.error("Max reconnection attempts reached (30 minutes).");
			this.isReconnecting = false;
			message.error("Failed to reconnect after multiple attempts. Please refresh the page.");
		}
	}

	// Modified initializeWithInitialData method to handle socket close events
	public initializeWithInitialData(reportIterationId: string): Promise<any> {
		// If we're already initializing for the same report, return the existing promise
		if (this.initializationPromise && this.currentReportIterationId === reportIterationId) {
			return this.initializationPromise;
		}

		// If we're already connected to the same report, return resolved promise
		if (this.socket && this.socket.readyState === WebSocket.OPEN && this.currentReportIterationId === reportIterationId) {
			return Promise.resolve({ message: "Already connected" });
		}

		// If we're currently connecting, wait for that to complete
		if (this.isConnecting || this.reconnectionLock) {
			return new Promise((resolve) => {
				const checkInterval = setInterval(() => {
					if (!this.isConnecting && !this.reconnectionLock) {
						clearInterval(checkInterval);
						if (this.socket && this.socket.readyState === WebSocket.OPEN) {
							resolve({ message: "Connection established" });
						} else {
							resolve(this.initializeWithInitialData(reportIterationId));
						}
					}
				}, 100);
			});
		}

		this.initializationPromise = new Promise((resolve, reject) => {
			// Rest of the existing implementation...
			let isSettled = false;

			const cleanup = () => {
				if (!isSettled) {
					isSettled = true;
					this.off("initial_report_data", handleInitialMessage);
					this.off("error", handleErrorMessage);
					this.off("socket_close", handleSocketClose);
					// Only clear initialization promise after a small delay to prevent race conditions
					setTimeout(() => {
						if (this.initializationPromise === currentPromise) {
							this.initializationPromise = null;
						}
					}, 100);
				}
			};

			// Store reference to current promise
			const currentPromise = this.initializationPromise;

			const handleInitialMessage = (data: WebSocketEvent) => {
				if (data.event === "initial_report_data") {
					cleanup();
					resolve(data);
				}
			};

			const handleErrorMessage = (data: WebSocketEvent) => {
				if (data.event === "error" || data.type === "error") {
					cleanup();
					console.error("[WebSocketReportService] Error received, rejecting promise", data.message);
					reject(new Error(data.message));
				}
			};

			const handleSocketClose = (data: WebSocketEvent) => {
				if (data.code === 1003) {
					cleanup();
					const socketCloseError = new Error(data.reason || "Socket connection closed with code 1003");
					socketCloseError.name = "SocketCloseError";
					// @ts-ignore
					socketCloseError.code = 1003;
					console.error("[WebSocketReportService] Socket closed with code 1003, rejecting promise", data.reason);
					reject(socketCloseError);
				}
			};

			// Register handlers
			this.on("initial_report_data", handleInitialMessage);
			this.on("error", handleErrorMessage);
			this.on("socket_close", handleSocketClose);

			// Start the connection with timeout
			const connectionTimeout = setTimeout(() => {
				cleanup();
				reject(new Error("Connection timeout"));
			}, 30000); // 30 second timeout

			this.connect(reportIterationId)
				.then(() => {
					clearTimeout(connectionTimeout);
				})
				.catch((err) => {
					clearTimeout(connectionTimeout);
					cleanup();
					console.error("[WebSocketReportService] Connection failed in initializeWithInitialData", err);
					reject(err);
				});
		});

		return this.initializationPromise;
	}

	// Report-specific update methods - All now check for valid reportIterationId

	public createSection(section: Partial<IReportSection>, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"section_create",
			{
				type: "section_create",
				report_iteration_id: this.currentReportIterationId,
				data: { section },
				// timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}

	public deleteSection(sectionId: string, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"section_delete",
			{
				type: "section_delete",
				report_iteration_id: this.currentReportIterationId,
				data: { section_id: sectionId },
				// timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}

	public updateGlobalDataSources(globalDataSources: IDataSources, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"global_data_sources_update",
			{
				type: "global_data_sources_update",
				report_iteration_id: this.currentReportIterationId,
				data: { global_data_sources: globalDataSources },
				timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}

	public updateSectionDataSources(sectionId: string, dataSources: IDataSources, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"section_data_sources_update",
			{
				type: "section_data_sources_update",
				report_iteration_id: this.currentReportIterationId,
				data: {
					section_id: sectionId,
					data_sources: dataSources,
				},
				timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}

	public updateSection(section: Partial<IReportSection>, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"section_update",
			{
				type: "section_update",
				report_iteration_id: this.currentReportIterationId,
				data: { section },
				timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}

	public updateGlobalInstructions(globalInstruction: string, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"global_instructions_update",
			{
				type: "global_instructions_update",
				report_iteration_id: this.currentReportIterationId,
				data: {
					global_instruction: {
						additional_instructions: globalInstruction,
					},
					// global_instruction: globalInstruction
				},
				timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}

	public reorderSections(payload: Array<{ section_id: string; new_order: number }>, callbacks?: { onSuccess?: (data: any) => void; onError?: (data: any) => void }): boolean {
		return this.sendWithCallbacks(
			"sections_reorder",
			{
				type: "sections_reorder",
				report_iteration_id: this.currentReportIterationId,
				data: { section_orders: payload },
				timestamp: new Date().toISOString(),
			},
			callbacks,
		);
	}
}

// Create a singleton instance
const websocketReportService = new WebSocketReportService();

export default websocketReportService;
